import { Layout, Menu, Typography, Button, Dropdown, Avatar, Space, Badge } from 'antd';
import { HomeOutlined, ClockCircleOutlined, HeartOutlined, CalendarOutlined, SettingOutlined, LoginOutlined, LogoutOutlined, UserOutlined, DownOutlined, MenuOutlined, FileImageOutlined, MonitorOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { PrayerTimes } from './components/PrayerTimes';
import { Zikr } from './components/Zikr';
import Events from './components/Events';
import HijriOffsetSettings from './components/HijriOffsetSettings';
// import { BrandingSettings } from './components/BrandingSettings';
import { Home } from './components/Home';
import MediaComponent from './components/Media';
import { TVPreview } from './components/TVPreview';
import { useAuthState } from './hooks/useAuthState';
import { apiMethods } from './services/api';
import { useState, useEffect, useCallback } from 'react';

const { Header, Content } = Layout;

interface MenuItem {
    key: string;
    icon: React.ReactNode;
    label: string;
    compactLabel?: string;
    requiresAuth?: boolean;
}

function App() {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, loginWithRedirect, logout, isLoading, user } = useAuthState();
    const [isCompact, setIsCompact] = useState(false);
    const [connectedTvs, setConnectedTvs] = useState<number | null>(null);
    const [refreshing, setRefreshing] = useState(false);

    const fetchTvConnections = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const data = await apiMethods.getTvConnections();
            setConnectedTvs(data.connected_clients);
        } catch {
            setConnectedTvs(null);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        fetchTvConnections();
        const interval = setInterval(fetchTvConnections, 30_000);
        return () => clearInterval(interval);
    }, [fetchTvConnections]);

    const handleTvRefresh = async (type: 'soft' | 'hard') => {
        setRefreshing(true);
        try {
            const result = await apiMethods.triggerTvRefresh(type);
            toast.success(`${result.message} (${result.connected_clients} TV${result.connected_clients !== 1 ? 's' : ''} connected)`);
            fetchTvConnections();
        } catch {
            toast.error('Failed to send refresh command');
        } finally {
            setRefreshing(false);
        }
    };

    // Check screen width and set compact mode
    useEffect(() => {
        const checkScreenWidth = () => {
            setIsCompact(window.innerWidth < 1360);
        };

        // Check initial screen width
        checkScreenWidth();

        // Add event listener for resize
        window.addEventListener('resize', checkScreenWidth);

        // Cleanup
        return () => window.removeEventListener('resize', checkScreenWidth);
    }, []);

    // Show loading while Auth0 is initializing
    if (isLoading) {
        return (
            <Layout className="min-h-screen">
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-lg text-gray-600">Loading...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    const allMenuItems: MenuItem[] = [
        { key: '/', icon: <HomeOutlined />, label: 'Home' },
        { key: '/tv-preview', icon: <MonitorOutlined />, label: 'TV Preview', requiresAuth: true },
        { key: '/prayer-times', icon: <ClockCircleOutlined />, label: 'Prayer Times', requiresAuth: true },
        { key: '/events', icon: <CalendarOutlined />, label: 'Islamic Events', requiresAuth: true },
        { key: '/hijri-adjustment', icon: <SettingOutlined />, label: 'Hijri Settings', requiresAuth: true },
        { key: '/zikr', icon: <HeartOutlined />, label: 'Zikr', requiresAuth: true },
        // { key: '/branding', icon: <SettingOutlined />, label: 'Branding', requiresAuth: true },
        { key: '/media', icon: <FileImageOutlined />, label: 'Media', requiresAuth: true },
    ];

    const menuItems = allMenuItems
        .filter(item => !item.requiresAuth || (item.requiresAuth && isAuthenticated));

    // Create dropdown menu items for compact mode
    const compactMenuItems = menuItems.map(item => ({
        key: item.key,
        icon: item.icon,
        label: item.label,
        onClick: () => navigate(item.key),
    }));

    // User dropdown menu items
    const userMenuItems = [
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: (
                <div>
                    <div className="font-medium">{user?.name || user?.email}</div>
                    <div className="text-sm text-gray-500">{user?.email}</div>
                </div>
            ),
            disabled: true,
        },
        {
            type: 'divider' as const,
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            onClick: () => logout({ logoutParams: { returnTo: window.location.origin } }),
        },
    ];

    return (
        <Layout className="min-h-screen">
            <Header className="bg-white shadow">
                <div className="flex items-center justify-between max-w-full">
                    <Typography.Title 
                        level={4} 
                        className={`m-0 flex-shrink-0 ${isCompact ? 'text-base' : ''}`}
                    >
                        {'Solumen Admin'}
                    </Typography.Title>
                    
                    {/* Compact menu (hamburger dropdown) */}
                    {isCompact ? (
                        <div className="flex-1 flex justify-center">
                            <Dropdown
                                menu={{ items: compactMenuItems }}
                                placement="bottomCenter"
                                arrow
                                trigger={['click']}
                            >
                                <Button
                                    type="text"
                                    icon={<MenuOutlined />}
                                    className="flex items-center justify-center hover:bg-gray-100"
                                    style={{ 
                                        border: 'none',
                                        boxShadow: 'none',
                                        fontSize: '18px'
                                    }}
                                />
                            </Dropdown>
                        </div>
                    ) : (
                        /* Normal horizontal menu */
                        <div className="flex-1 flex justify-center min-w-0">
                            <Menu
                                mode="horizontal"
                                selectedKeys={[location.pathname]}
                                items={menuItems.map(({ requiresAuth, compactLabel, ...item }) => item)}
                                onClick={({ key }) => navigate(key)}
                                style={{ 
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    minWidth: 'fit-content',
                                    flex: 1,
                                    justifyContent: 'center'
                                }}
                                overflowedIndicator={null}
                            />
                        </div>
                    )}

                    <div className="flex-shrink-0 flex items-center gap-2">
                        {isAuthenticated && (
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: 'status',
                                            label: connectedTvs !== null
                                                ? `${connectedTvs} TV${connectedTvs !== 1 ? 's' : ''} connected`
                                                : 'No TVs connected',
                                            disabled: true,
                                        },
                                        { type: 'divider' as const, key: 'divider' },
                                        {
                                            key: 'soft',
                                            label: 'Soft Refresh (reload data)',
                                            onClick: () => handleTvRefresh('soft'),
                                        },
                                        {
                                            key: 'hard',
                                            label: 'Hard Refresh (full reload)',
                                            onClick: () => handleTvRefresh('hard'),
                                        },
                                    ],
                                }}
                                placement="bottomRight"
                                trigger={['click']}
                            >
                                <Badge count={connectedTvs ?? 0} size="small" offset={[-4, 4]}>
                                    <Button
                                        type="text"
                                        icon={<ReloadOutlined spin={refreshing} />}
                                        loading={refreshing}
                                        size={isCompact ? "small" : "middle"}
                                    />
                                </Badge>
                            </Dropdown>
                        )}
                        {isAuthenticated ? (
                            <Dropdown
                                menu={{ items: userMenuItems }}
                                placement="bottomRight"
                                arrow
                            >
                                <Space className={`cursor-pointer hover:bg-gray-50 rounded-lg transition-colors ${isCompact ? 'px-2 py-1' : 'px-3 py-2'}`}>
                                    <Avatar 
                                        src={user?.picture} 
                                        icon={<UserOutlined />}
                                        size={isCompact ? "small" : "default"}
                                    />
                                    <span className={`font-medium ${isCompact ? 'hidden' : 'hidden sm:inline'}`}>
                                        {user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'User'}
                                    </span>
                                    <DownOutlined className="text-xs" />
                                </Space>
                            </Dropdown>
                        ) : (
                            <Button
                                type="primary"
                                icon={<LoginOutlined />}
                                onClick={() => loginWithRedirect()}
                                size={isCompact ? "small" : "middle"}
                            >
                                {isCompact ? '' : 'Login'}
                            </Button>
                        )}
                    </div>
                </div>
            </Header>
            
            <Content className="p-6">
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/tv-preview" element={<TVPreview />} />
                    <Route path="/prayer-times" element={<PrayerTimes />} />
                    <Route path="/events" element={<Events />} />
                    <Route path="/hijri-adjustment" element={<HijriOffsetSettings />} />
                    <Route path="/zikr" element={<Zikr />} />
                    {/* <Route path="/branding" element={<BrandingSettings />} /> */}
                    <Route path="/media" element={<MediaComponent />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Content>
            <Toaster position="top-right" />
        </Layout>
    );
}

export default App; 